import { Button } from '@/components/ui/button'
import { isChunkLoadError } from '@/lib/lazyImport'
import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

interface ErrorBoundaryProps {
  componentName?: string
  children: ReactNode
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      `Error in ${this.props.componentName ?? 'component'}:`,
      error,
      errorInfo
    )
    this.setState({ errorInfo })
  }

  render() {
    if (this.state.hasError) {
      const chunkLoadError = isChunkLoadError(this.state.error)

      return (
        <div
          style={{
            padding: '10px',
            margin: '10px',
            border: '1px solid red',
            borderRadius: '4px',
          }}
        >
          <h3>
            {chunkLoadError
              ? 'A new version of MCCS is available'
              : `Error in ${this.props.componentName ?? 'component'}`}
          </h3>
          <p>
            {chunkLoadError
              ? 'Please refresh the page to continue. Any work you already submitted has been saved.'
              : 'Something went wrong loading this part of the page.'}
          </p>
          <Button size="sm" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
          {import.meta.env.DEV && (
            <details>
              <summary>Show Error Details (dev only)</summary>
              <pre>{this.state.error?.toString()}</pre>
              <pre>{this.state.errorInfo?.componentStack}</pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
